import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApi, useEngine } from '../api/context';
import { useMetaIndex } from '../meta/useMeta';
import { Portal, useEscape, useFocusTrap } from '../design/primitives/Overlay';
import { Icon, type IconName } from '../design/icons';
import { Kbd, keyLabel } from '../design/primitives/Misc';
import { labelOf } from '../screens/ResourceFormScreen';
import { can } from '../meta/types';
import type { CommandDef } from '../module';
import { useHotkeys, usePageActionList } from './shortcuts';

// ⌘K palette, built on the engine (no cmdk): the current page's actions (usePageActions), navigation
// from every module's nav, commands from ModuleUI.commands, "New <resource>" for creatable resources,
// and live record search. Prefixes: `>` actions and commands only, `@` records only. `.` (outside a
// text field) opens it as the page's quick-action menu (`>` prefilled). Record search goes
// through a module's `ModuleUI.search` provider when one is registered (one round trip, e.g.
// GET /v1/search), else fanned out across searchable resources via `q=` (first 5 per resource).

interface Item {
  id: string;
  label: string;
  kind: string;
  icon?: IconName;
  keywords?: string;
  shortcut?: string;
  run: () => void;
}

function score(item: Item, needle: string): number {
  if (!needle) return 1;
  const hay = `${item.label} ${item.keywords ?? ''} ${item.kind}`.toLowerCase();
  if (item.label.toLowerCase().startsWith(needle)) return 3;
  if (item.label.toLowerCase().includes(needle)) return 2;
  if (hay.includes(needle)) return 1;
  // Subsequence match on the label only ("inv" → "Invoices"), never on hints/keywords.
  if (needle.length < 3) return 0;
  let i = 0;
  for (const ch of item.label.toLowerCase()) if (ch === needle[i]) i++;
  return i === needle.length ? 0.5 : 0;
}

/** `>` = actions and commands only; `@` = records only; anything else = everything. */
export function parsePaletteQuery(raw: string): { mode: 'all' | 'actions' | 'records'; text: string } {
  const t = raw.trimStart();
  if (t.startsWith('>')) return { mode: 'actions', text: t.slice(1).trim() };
  if (t.startsWith('@')) return { mode: 'records', text: t.slice(1).trim() };
  return { mode: 'all', text: raw.trim() };
}

export function CommandPalette({ open: openProp, onClose: closeProp }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const api = useApi();
  const { modules } = useEngine();
  const [quick, setQuick] = useState(false);
  const open = openProp || quick;
  const onClose = () => {
    setQuick(false);
    closeProp();
  };
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  useFocusTrap(ref, open);
  useEscape(open, onClose);
  useHotkeys([{ keys: '.', run: () => setQuick(true), label: 'Quick actions for this page', group: 'General' }]);
  useEffect(() => {
    if (open) {
      setQ(quick ? '> ' : '');
      setSel(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset on open only
  }, [open]);
  const pageActions = usePageActionList();
  const parsed = parsePaletteQuery(q);

  const metaIndex = useMetaIndex();
  const resources = useMemo(() => (metaIndex.data?.resources ?? []).filter((r) => r.capabilities.list), [metaIndex.data]);

  // Map resource → list route from modules' screens.
  const routeFor = useMemo(() => {
    const m = new Map<string, { list?: string; detail?: string }>();
    for (const mod of modules)
      for (const s of mod.screens) {
        if (!s.resource) continue;
        const e = m.get(s.resource) ?? {};
        if (s.kind === 'list') e.list = s.path;
        if (s.kind === 'detail') e.detail = s.path;
        m.set(s.resource, e);
      }
    return m;
  }, [modules]);

  const go = (to: string) => () => {
    onClose();
    navigate(to);
  };

  const staticItems = useMemo<Item[]>(() => {
    const nav: Item[] = modules.flatMap((mod) => mod.nav.flatMap((sec) => sec.items.map((it) => ({ id: `nav:${it.to}`, label: it.label, kind: 'go to', icon: it.icon, keywords: sec.label, shortcut: it.key ? `g ${it.key}` : undefined, run: go(it.to) }))));
    const cmds: Item[] = modules.flatMap((mod) =>
      (mod.commands ?? []).map((c: CommandDef) => ({
        id: `cmd:${c.id}`,
        label: c.label,
        kind: c.kind ?? 'command',
        icon: c.icon,
        keywords: c.keywords,
        shortcut: c.shortcut,
        run: () => {
          onClose();
          c.run({ navigate, close: onClose, create: (r) => navigate(`${routeFor.get(r)?.list ?? '/'}?new=1`) });
        },
      })),
    );
    const create: Item[] = resources
      .filter((r) => r.capabilities.create && can(r, 'create') && routeFor.get(r.name)?.list)
      .map((r) => ({ id: `new:${r.name}`, label: `New ${r.label.toLowerCase()}…`, kind: 'create', icon: 'plus', keywords: r.labelPlural, run: go(`${routeFor.get(r.name)!.list}?new=1`) }));
    return [...cmds, ...create, ...nav];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onClose is a fresh closure each render
  }, [modules, resources, routeFor, navigate]);

  // The page's own actions, first: what you can do HERE.
  const actionItems = useMemo<Item[]>(
    () => pageActions.map((h) => ({ id: `page:${h.pageAction!.id}`, label: h.label ?? h.pageAction!.id, kind: 'this page', icon: h.pageAction!.icon ?? 'zap', shortcut: h.keys || undefined, run: () => { onClose(); h.run(new KeyboardEvent('keydown')); } })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageActions],
  );

  // Live record search across searchable resources (debounced).
  const [needle, setNeedle] = useState('');
  useEffect(() => {
    const t = window.setTimeout(() => setNeedle(parsed.mode === 'actions' ? '' : parsed.text), 180);
    return () => window.clearTimeout(t);
  }, [parsed.mode, parsed.text]);
  const searchable = resources.filter((r) => r.searchFields.length > 0 && routeFor.get(r.name));
  const provider = useMemo(() => modules.map((m) => m.search).find((s) => typeof s === 'function'), [modules]);
  const records = useQuery({
    queryKey: ['palette', needle, provider ? 'provider' : searchable.map((r) => r.name)],
    enabled: open && needle.length >= 2,
    staleTime: 15_000,
    queryFn: async () => {
      const out: Item[] = [];
      if (provider) {
        for (const h of await provider(api, needle)) out.push({ id: h.id, label: h.label, kind: h.kind, icon: h.icon ?? 'file', keywords: h.keywords, run: go(h.to) });
        return out;
      }
      await Promise.all(
        searchable.map(async (r) => {
          try {
            const page = await api.resource(r.name).list({ q: needle, limit: 5 });
            for (const row of page.data) {
              const id = String(row[r.idField]);
              const route = routeFor.get(r.name)!;
              const to = route.detail ? route.detail.replace(':id', id) : `${route.list}?filter[${r.idField}][eq]=${id}`;
              out.push({ id: `${r.name}:${id}`, label: labelOf(r, row), kind: r.label.toLowerCase(), icon: (r.icon as IconName) ?? 'file', keywords: row.status ? String(row.status) : undefined, run: go(to) });
            }
          } catch {
            /* a resource that doesn't support q= is skipped */
          }
        }),
      );
      return out;
    },
  });

  const items = useMemo(() => {
    const n = parsed.text.toLowerCase();
    const rank = (list: Item[]) => list.map((it) => ({ it, s: score(it, n) })).filter((x) => x.s > 0).sort((a, b) => b.s - a.s).map((x) => x.it);
    const hits = needle.length >= 2 ? (records.data ?? []) : [];
    if (parsed.mode === 'records') return hits;
    if (parsed.mode === 'actions') return [...rank(actionItems), ...rank(staticItems.filter((i) => i.kind !== 'go to' && i.kind !== 'create'))].slice(0, 30);
    const page = rank(actionItems).slice(0, n ? 5 : 6);
    return [...page, ...rank(staticItems).slice(0, n ? 8 : 12), ...hits];
  }, [staticItems, actionItems, parsed.mode, parsed.text, needle, records.data]);

  useEffect(() => setSel(0), [q]);
  useEffect(() => {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [sel]);

  if (!open) return null;
  return (
    <Portal>
      <div className="hf-palette-root">
        <div className="hf-scrim blur" onMouseDown={(e) => e.target === e.currentTarget && onClose()} />
        <div ref={ref} className="hf-palette" role="dialog" aria-modal="true" aria-label="Command palette">
          <div className="hf-palette-input">
            <Icon name="command" size={16} className="hf-muted" />
            <input
              data-autofocus
              role="combobox"
              aria-expanded
              aria-controls="hf-palette-list"
              aria-activedescendant={items[sel] ? `hf-pi-${items[sel]!.id}` : undefined}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Jump to a page or record, or run a command…  (> actions · @ records)"
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setSel((s) => Math.min(s + 1, items.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setSel((s) => Math.max(s - 1, 0));
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  items[sel]?.run();
                }
              }}
            />
            {records.isFetching && <span className="hf-spinner" aria-label="Searching" />}
          </div>
          <div id="hf-palette-list" ref={listRef} role="listbox" className="hf-palette-list hf-scroll">
            {items.length === 0 ? (
              <div className="hf-palette-empty">{needle.length >= 2 && records.isFetching ? 'Searching…' : parsed.mode === 'records' && needle.length < 2 ? 'Type at least two characters to search records.' : parsed.mode === 'actions' && !parsed.text && actionItems.length === 0 ? 'This page has no actions.' : `Nothing matches “${parsed.text || q}”.`}</div>
            ) : (
              items.map((it, i) => (
                <div key={it.id} id={`hf-pi-${it.id}`} role="option" aria-selected={i === sel} className={['hf-palette-item', i === sel && 'selected'].filter(Boolean).join(' ')} onMouseEnter={() => setSel(i)} onMouseDown={(e) => e.preventDefault()} onClick={it.run}>
                  <Icon name={it.icon ?? 'arrow-right'} size={14} className="hf-muted" />
                  <span className="lbl">{it.label}</span>
                  {it.shortcut && <span className="sc">{it.shortcut.split(' ').map((k) => <Kbd key={k}>{keyLabel(k)}</Kbd>)}</span>}
                  <span className="kind">{it.kind}</span>
                </div>
              ))
            )}
          </div>
          <div className="hf-palette-hint">
            <span><Kbd>↑↓</Kbd> navigate</span>
            <span><Kbd>↵</Kbd> open</span>
            <span><Kbd>esc</Kbd> close</span>
            <span><Kbd>&gt;</Kbd> actions</span>
            <span><Kbd>@</Kbd> records</span>
            <span style={{ marginLeft: 'auto' }}><Kbd>?</Kbd> all shortcuts</span>
          </div>
        </div>
      </div>
    </Portal>
  );
}
